import { Outlet, useNavigate } from "@tanstack/react-router"
import { DateRange } from "design-system/molecules/DatePicker"
import { useHasCompanyFeature } from "modules/auth/hooks/useHasCompanyFeature"
import { useConversationsQuery } from "modules/conversations/hooks/useConversationsQuery"
import { CanonicalTopicsTimeline } from "modules/insights/components/CanonicalTopicsTimeline"
import { Pagination } from "modules/shared/components/Pagination"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSortBy } from "modules/shared/hooks/useSortBy"
import { Route as ConversationsListRoute } from "routes/_base/_auth/conversations/list/route"
import {
  ClusterDomainEnum,
  ClusterDomainSchema,
  CompanyUsageKind,
  GetConversationsSortFields,
} from "schema"
import z from "zod"
import { ConversationsFilter } from "./ConversationsFilter"
import { ConversationsTable } from "./ConversationsTable"

const ITEMS_PER_PAGE = 50

export function Conversations() {
  const navigate = useNavigate()
  const hasFeedback = useHasCompanyFeature(CompanyUsageKind.Feedback)

  const search = ConversationsListRoute.useSearch()

  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: ConversationsListRoute.fullPath,
    total: ITEMS_PER_PAGE,
  })

  const { onSort, sortBy, fields } = useSortBy<typeof GetConversationsSortFields>({
    search,
    fullPath: ConversationsListRoute.fullPath,
    fields: GetConversationsSortFields,
  })

  const { data: conversations, isPending } = useConversationsQuery({
    query: {
      query: search.query,
      page,
      total: ITEMS_PER_PAGE,
      sortBy,
      filterBy: {
        ...search.filterBy,
      },
    },
    onSuccess: (data) => {
      setCount(data.pageInfo.count)
    },
  })

  function onApplyClusterFilters({
    domain,
    segmentId,
    goalId,
    canonicalId,
    dateRange,
  }: {
    domain: z.infer<typeof ClusterDomainSchema> | undefined
    segmentId: string | undefined
    goalId: string | undefined
    canonicalId: string | undefined
    dateRange: DateRange | undefined
  }) {
    const newFilterBy = { ...search.filterBy }

    if (domain) newFilterBy.domain = domain
    else delete newFilterBy.domain

    if (segmentId) newFilterBy.segmentId = [segmentId]
    else delete newFilterBy.segmentId

    if (goalId) newFilterBy.goalId = [goalId]
    else delete newFilterBy.goalId

    if (canonicalId) newFilterBy.canonicalId = canonicalId
    else delete newFilterBy.canonicalId

    if (dateRange && dateRange.from) {
      newFilterBy.dateRange = {
        from: dateRange.from,
        to: dateRange.to,
      }
    } else {
      delete newFilterBy.dateRange
    }

    navigate({ to: ConversationsListRoute.fullPath, search: { ...search, filterBy: newFilterBy } })
  }

  return (
    <div className="flex flex-col gap-6 w-full h-full">
      {hasFeedback && (
        <CanonicalTopicsTimeline
          availableDomains={[ClusterDomainEnum.VoiceOfCustomer, ClusterDomainEnum.Goal]}
          domain={search.filterBy?.domain ?? ClusterDomainEnum.VoiceOfCustomer}
          segmentId={search.filterBy?.segmentId?.[0]}
          goalId={search.filterBy?.goalId?.[0]}
          canonicalId={search.filterBy?.canonicalId}
          dateRange={search.filterBy?.dateRange}
          onApplyFilters={onApplyClusterFilters}
        />
      )}
      <div className="flex flex-col gap-4 h-full">
        <ConversationsFilter />
        <ConversationsTable
          data={conversations?.data}
          isPending={isPending}
          fields={fields}
          onSort={onSort}
          search={search}
          showCampaign={true}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          count={count}
        />
      </div>
      <Outlet />
    </div>
  )
}
